/**
 * Saisie d'une vente BtoB — création ou modification.
 *
 * Le nom du magasin propose ceux déjà saisis (`datalist`) : taper « Fn »
 * suggère « Fnac Lyon », et le même magasin n'apparaît pas trois fois sous
 * trois orthographes. L'API regroupe de toute façon sans tenir compte de la
 * casse.
 */

import {
  BTOB_DEFAULT_VAT_RATE,
  BTOB_INVOICE_MAX_LENGTH,
  BTOB_NOTE_MAX_LENGTH,
  BTOB_STORE_MAX_LENGTH,
  type BtobSale,
  type BtobSaleInput,
} from '@shared/types/btob';
import { useEffect, useState, type FormEvent } from 'react';

import { todayInParis } from '../lib/dates';
import { Card } from './Card';

interface BtobSaleFormProps {
  /** Vente modifiée, ou `null` pour une nouvelle saisie. */
  editing: BtobSale | null;
  knownStores: readonly string[];
  isSaving: boolean;
  onSubmit: (input: BtobSaleInput) => Promise<void>;
  onCancel: () => void;
}

interface Draft {
  storeName: string;
  invoiceNumber: string;
  invoiceDate: string;
  paymentDate: string;
  units: string;
  revenueHt: string;
  vatRate: string;
  note: string;
}

const emptyDraft = (): Draft => ({
  storeName: '',
  invoiceNumber: '',
  invoiceDate: todayInParis(),
  paymentDate: '',
  units: '',
  revenueHt: '',
  vatRate: String(BTOB_DEFAULT_VAT_RATE),
  note: '',
});

const toDraft = (sale: BtobSale): Draft => ({
  storeName: sale.storeName,
  invoiceNumber: sale.invoiceNumber ?? '',
  invoiceDate: sale.invoiceDate,
  paymentDate: sale.paymentDate ?? '',
  units: String(sale.units),
  revenueHt: String(sale.revenueHt).replace('.', ','),
  vatRate: String(sale.vatRate).replace('.', ','),
  note: sale.note ?? '',
});

/** « 1 250,50 » → 1250.5 ; `null` si illisible. */
const parseNumber = (raw: string): number | null => {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};

export function BtobSaleForm({ editing, knownStores, isSaving, onSubmit, onCancel }: BtobSaleFormProps) {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(editing ? toDraft(editing) : emptyDraft());
    setError(null);
  }, [editing]);

  const set = (key: keyof Draft) => (event: { target: { value: string } }) =>
    setDraft((current) => ({ ...current, [key]: event.target.value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const units = parseNumber(draft.units);
    const revenueHt = parseNumber(draft.revenueHt);
    const vatRate = parseNumber(draft.vatRate);
    if (units === null || !Number.isInteger(units) || units < 0) return setError('Unités : un nombre entier.');
    if (revenueHt === null || revenueHt < 0) return setError('CA HT : un montant positif.');
    if (vatRate === null || vatRate < 0 || vatRate > 30) return setError('TVA : entre 0 et 30 %.');

    setError(null);
    try {
      await onSubmit({
        storeName: draft.storeName,
        invoiceNumber: draft.invoiceNumber || null,
        invoiceDate: draft.invoiceDate,
        paymentDate: draft.paymentDate || null,
        units,
        revenueHt,
        vatRate,
        note: draft.note || null,
      });
      if (!editing) setDraft((current) => ({ ...emptyDraft(), invoiceDate: current.invoiceDate }));
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Enregistrement impossible.');
    }
  };

  return (
    <Card
      title={editing ? 'Modifier la vente' : 'Ajouter une vente'}
      subtitle="La date de facture rattache la vente à une période ; la date de paiement dit ce qui reste à encaisser."
      className="marker-form-card"
    >
      <form className="marker-form" onSubmit={(event) => void handleSubmit(event)}>
        <div className="field marker-form__grow">
          <label className="field__label" htmlFor="btob-store">Magasin</label>
          <input
            id="btob-store"
            type="text"
            list="btob-known-stores"
            required
            maxLength={BTOB_STORE_MAX_LENGTH}
            value={draft.storeName}
            onChange={set('storeName')}
            placeholder="ex. Fnac Lyon"
          />
          <datalist id="btob-known-stores">
            {knownStores.map((store) => (
              <option key={store} value={store} />
            ))}
          </datalist>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="btob-invoice">N° de facture</label>
          <input id="btob-invoice" type="text" maxLength={BTOB_INVOICE_MAX_LENGTH} value={draft.invoiceNumber} onChange={set('invoiceNumber')} style={{ width: 130 }} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="btob-invoice-date">Date de facture</label>
          <input id="btob-invoice-date" type="date" required value={draft.invoiceDate} onChange={set('invoiceDate')} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="btob-payment-date">Date de paiement</label>
          <input id="btob-payment-date" type="date" value={draft.paymentDate} onChange={set('paymentDate')} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="btob-units">Unités</label>
          <input id="btob-units" type="text" inputMode="numeric" required value={draft.units} onChange={set('units')} style={{ width: 80 }} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="btob-revenue">CA HT (€)</label>
          <input id="btob-revenue" type="text" inputMode="decimal" required value={draft.revenueHt} onChange={set('revenueHt')} style={{ width: 110 }} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="btob-vat">TVA (%)</label>
          <input id="btob-vat" type="text" inputMode="decimal" value={draft.vatRate} onChange={set('vatRate')} style={{ width: 64 }} />
        </div>
        <div className="field marker-form__grow">
          <label className="field__label" htmlFor="btob-note">Note</label>
          <input id="btob-note" type="text" maxLength={BTOB_NOTE_MAX_LENGTH} value={draft.note} onChange={set('note')} />
        </div>
        <div className="marker-form__actions">
          <button type="submit" className="btn btn--primary" disabled={isSaving}>
            {isSaving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Ajouter'}
          </button>
          {editing && (
            <button type="button" className="btn" onClick={onCancel} disabled={isSaving}>
              Annuler
            </button>
          )}
        </div>
        {error && (
          <p className="login__error marker-form__error" role="alert">
            {error}
          </p>
        )}
      </form>
    </Card>
  );
}
